import { useEffect } from "react";

import {
  SESSION_ACTIVITY_PING_AFTER_MS,
  SESSION_ACTIVITY_PING_PATH,
  SESSION_ACTIVITY_PING_RETRY_MS,
  SESSION_ACTIVITY_WRITE_THROTTLE_MS,
  SESSION_INACTIVITY_CHECK_INTERVAL_MS,
  SESSION_INACTIVITY_TIMEOUT_MS,
} from "@/constants/session";
import { apiFetch, setSessionRefreshEnabled } from "@/lib/api";
import { markUserActivity, readLastApiActivity, readLastUserActivity } from "@/lib/sessionActivity";
import {
  settlePendingLogout,
  trackPendingLogout,
  triggerSessionExpired,
} from "@/lib/sessionExpiry";

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;

export function useInactivityLogout(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    let expiring = false;
    let inMemoryLastActivityAt = Date.now();
    let lastActivityWriteAt = 0;
    let lastPingAttemptAt = 0;
    let pingInFlight = false;

    markUserActivity(inMemoryLastActivityAt);

    const maybePingActivity = (now: number) => {
      const lastApiAt = readLastApiActivity() ?? 0;
      if (now - lastApiAt < SESSION_ACTIVITY_PING_AFTER_MS) return;
      if (now - lastPingAttemptAt < SESSION_ACTIVITY_PING_RETRY_MS) return;
      if (pingInFlight) return;

      pingInFlight = true;
      lastPingAttemptAt = now;
      apiFetch<unknown>(SESSION_ACTIVITY_PING_PATH)
        .catch(() => undefined)
        .finally(() => {
          pingInFlight = false;
        });
    };

    const handleActivity = () => {
      if (expiring) return;

      const now = Date.now();
      inMemoryLastActivityAt = now;
      if (now - lastActivityWriteAt < SESSION_ACTIVITY_WRITE_THROTTLE_MS) return;

      lastActivityWriteAt = now;
      markUserActivity(now);
      maybePingActivity(now);
    };

    const expireSession = () => {
      if (expiring) return;
      expiring = true;

      setSessionRefreshEnabled(false);
      const logoutSignal = trackPendingLogout();
      void apiFetch<unknown>("/v1/auth/logout", { method: "POST", signal: logoutSignal })
        .catch(() => undefined)
        .finally(() => settlePendingLogout(logoutSignal));
      triggerSessionExpired("inactivity");
    };

    const checkInactivity = () => {
      if (expiring) return;

      const lastActivityAt = Math.max(readLastUserActivity() ?? 0, inMemoryLastActivityAt);
      if (Date.now() - lastActivityAt >= SESSION_INACTIVITY_TIMEOUT_MS) {
        expireSession();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkInactivity();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true, capture: true })
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", checkInactivity);
    const intervalId = window.setInterval(checkInactivity, SESSION_INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity, { capture: true })
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", checkInactivity);
      window.clearInterval(intervalId);
    };
  }, [enabled]);
}
